import api from '../api/api';
import GetIntentsConfig from '../../types/GetIntentsConfig';
import { IntentSearchResult } from '../../types/IntentSearchResult';
import IntentResponse from '../../types/BotBuilder/IntentResponse';
import Chatbot from '../../types/Chatbot';
import Slot from '../../types/BotBuilder/Slot';
import BulkSimpleIntentPayload from '../../types/BotBuilder/BulkSimpleIntentPayload';
import BulkImportResult from '../../types/BotBuilder/BulkImportResult';

class BotBuilderService {
  static async getIntents({
    chatbotId,
    query,
    page,
    resultsPerPage,
    exact,
    nameOnly
  }: GetIntentsConfig): Promise<IntentSearchResult> {
    const baseUrl = `chatbots/${chatbotId}/intents`;

    //  Append search params from arguments
    const searchParams = new URLSearchParams();
    searchParams.append('page', page.toString());
    searchParams.append('resultsPerPage', resultsPerPage.toString());
    searchParams.append('q', query);
    if (exact !== undefined) {
      searchParams.append('exact', exact.toString());
    }
    if (nameOnly !== undefined) {
      searchParams.append('nameOnly', nameOnly.toString());
    }

    //  Retrieve data
    const apiResponse = await api.get(`${baseUrl}?${searchParams}`);
    const intentsSearchResult: IntentSearchResult = apiResponse.data;
    return intentsSearchResult;
  }

  static async getIntent(
    chatbotId: number,
    intentId: number
  ): Promise<IntentResponse> {
    const baseUrl = `chatbots/${chatbotId}/intents/${intentId}`;
    const apiResponse = await api.get(`${baseUrl}`);
    return apiResponse.data;
  }

  static async updateIntent(
    chatbotId: number,
    intent: IntentResponse
  ): Promise<IntentResponse> {
    const baseUrl = `chatbots/${chatbotId}/intents/${intent.id}`;
    const apiResponse = await api.put(`${baseUrl}`, intent);
    return apiResponse.data;
  }

  static async createNewIntent(
    chatbotId: number,
    intent: IntentResponse
  ): Promise<IntentResponse> {
    const baseUrl = `chatbots/${chatbotId}/intents`;
    const apiResponse = await api.post(`${baseUrl}`, intent);
    return apiResponse.data;
  }

  static async bulkCreateIntents(
    chatbotId: number,
    intents: BulkSimpleIntentPayload[]
  ): Promise<BulkImportResult> {
    const baseUrl = `chatbots/${chatbotId}/intents/bulk`;
    const apiResponse = await api.post(`${baseUrl}`, intents);
    return apiResponse.data;
  }

  static async deleteIntent(
    chatbotId: number,
    intentId: number
  ): Promise<IntentResponse> {
    const baseUrl = `chatbots/${chatbotId}/intents/${intentId}`;
    const apiResponse = await api.delete(`${baseUrl}`);
    return apiResponse.data;
  }

  static async getTopics(chatbotId: number): Promise<any> {
    const baseUrl = `chatbots/${chatbotId}/topics`;
    const apiResponse = await api.get(`${baseUrl}`);
    return apiResponse.data;
  }

  static async getChatbot(
    chatbotId: number,
    signal?: AbortSignal
  ): Promise<Chatbot> {
    const baseUrl = `chatbots/${chatbotId}`;

    const config: { signal?: AbortSignal } = {};

    if (signal) {
      config.signal = signal;
    }

    const apiResponse = await api.get(baseUrl, config);
    return apiResponse.data;
  }

  static async createChatbot(
    organisationId: number,
    name: string
  ): Promise<Pick<Chatbot, 'id' | 'uuid' | 'name'>> {
    const apiResponse = await api.post('chatbots', { organisationId, name });
    return apiResponse.data;
  }

  static async getChatbotsInfo(
    organisationId: number,
    signal?: AbortSignal
  ): Promise<Chatbot[]> {
    const baseUrl = `chatbots/info/${organisationId}`;

    const config: { signal?: AbortSignal } = {};

    if (signal) {
      config.signal = signal;
    }

    const apiResponse = await api.get(baseUrl, config);
    return apiResponse.data;
  }

  static async getChatbots(
    organisationId: number,
    signal?: AbortSignal
  ): Promise<Chatbot[]> {
    const baseUrl = `chatbots/all/${organisationId}`;

    const config: { signal?: AbortSignal } = {};

    if (signal) {
      config.signal = signal;
    }

    const apiResponse = await api.get(baseUrl, config);
    return apiResponse.data;
  }

  static async buildChatbot(chatbotId: number): Promise<Chatbot> {
    const baseUrl = `chatbots/${chatbotId}/build`;

    const apiResponse = await api.post(baseUrl);
    return apiResponse.data;
  }

  static async createSlot(chatbotId: number, slot: Slot): Promise<Slot> {
    const baseUrl = `chatbots/${chatbotId}/slots`;
    const apiResponse = await api.post(`${baseUrl}`, slot);
    return apiResponse.data;
  }

  static async getSlots(chatbotId: number): Promise<Slot[]> {
    const baseUrl = `chatbots/${chatbotId}/slots`;
    const apiResponse = await api.get(`${baseUrl}`);
    return apiResponse.data;
  }

  static async deleteSlot(chatbotId: number, slotId: number): Promise<void> {
    const baseUrl = `chatbots/${chatbotId}/slots/${slotId}`;
    await api.delete(`${baseUrl}`);
  }

  static async updateSlot(chatbotId: number, slot: Slot): Promise<Slot> {
    const baseUrl = `chatbots/${chatbotId}/slots/${slot.id}`;
    const apiResponse = await api.put(`${baseUrl}`, slot);
    return apiResponse.data;
  }

  static async deleteChatbot(chatbotId: number): Promise<void> {
    const baseUrl = `chatbots/${chatbotId}`;
    await api.delete(`${baseUrl}`);
  }
}

export default BotBuilderService;
